import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { IndirizzoAutocompleteService } from '../http/indirizzo-autocomplete.service';
import { IndirizzoDto } from '../models/indirizzo-dto.model';

/**
 * State type.
 */
type IndirizzoAutocompleteState = {
  indirizzi: IndirizzoDto[];
  isLoading: boolean;
  hasError: boolean;
};

/**
 * Initial state.
 */
const initialState: IndirizzoAutocompleteState = {
  indirizzi: [],
  isLoading: false,
  hasError: false,
};

export const IndirizzoAutocompleteStore = signalStore(
  withState<IndirizzoAutocompleteState>(initialState),
  withMethods(store => {
    /**
     * Set indirizzi found.
     */
    const _setIndirizzi = (indirizzi: IndirizzoDto[]): void => {
      patchState(store, { indirizzi, hasError: false, isLoading: false });
    };

    /**
     * Set error.
     */
    const _setError = (hasError: boolean): void => {
      patchState(store, { hasError, isLoading: false, indirizzi: [] });
    };

    /**
     * Clear the found indirizzi (e.g. query below the minimum length).
     */
    const clearIndirizzi = (): void => {
      patchState(store, { indirizzi: [], hasError: false, isLoading: false });
    };

    return {
      _setIndirizzi,
      _setError,
      clearIndirizzi,
    };
  }),
  withMethods(
    (
      store,
      indirizzoAutocompleteService = inject(IndirizzoAutocompleteService)
    ) => {
      /**
       * Search indirizzi by query.
       */
      const searchIndirizzi = rxMethod<{ query: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ query }) =>
            indirizzoAutocompleteService.autocomplete(query).pipe(
              tapResponse({
                next: (response: IndirizzoDto[]) =>
                  store._setIndirizzi(response),
                error: () => store._setError(true),
              })
            )
          )
        )
      );

      return {
        searchIndirizzi,
      };
    }
  )
);
